"use client";
import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";

const dataChart1 = [
  {
    name: "Current\nRate",
    value: 1.4,
    displayValue: "~1.4%",
    color: "#48DBB2",
  },
  { name: "2030\nTarget", value: 2.8, displayValue: "~2.8%", color: "#003350" },
];

const dataChart2 = [
  { name: "2025", value: 100, displayValue: "100", color: "#003350" },
  { name: "2030\nTarget", value: 75, displayValue: "75", color: "#003350" },
];

const dataChart3 = [
  { name: "2025", value: 23, displayValue: "23%", color: "#48DBB2" },
  { name: "2030\nTarget", value: 15, displayValue: "15%", color: "#48DBB2" },
];

const dataChart4 = [
  { name: "0% Saving", value: 0, displayValue: "0", color: "#003350" },
  { name: "1% Saving", value: 1, displayValue: "1", color: "#003350" },
];

const CustomXAxisTick = (props: any) => {
  const { x, y, payload } = props;
  const lines = payload.value.split("\n");
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="middle" fill="#4B5563" fontSize="9" dy={8}>
        {lines.map((line: string, index: number) => (
          <tspan x={0} dy={index === 0 ? 0 : 10} key={index}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

export default function MultipleBarChart({ isActive }: { isActive: boolean }) {
  const [show, setShow] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (isActive) {
      setTimeout(() => setShow(true), 200);
    } else {
      setShow(false);
    }

    const handleResize = () => {
      setIsMobile(window.innerWidth < 1170);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isActive]);

  return (
    <div
      className="w-full flex bg-[#f9f9f9] font-sans items-center justify-center p-1 md:p-2 overflow-hidden"
      style={{ height: isMobile ? 380 : "100%" }}
    >
      <div className="w-full h-[95%] max-w-[1000px] flex flex-col relative">
        {/* Charts Container */}
        <div className="flex flex-row w-full flex-1 gap-1">
          {/* Chart 1 */}
          <div className="flex-1 flex flex-col relative border-r border-[#003350] px-1 h-full">
            <h3 className="text-[#003350] font-bold text-center text-[12px] lg:text-[14px] leading-[1.15] min-h-[50px] flex items-center justify-center">
              Double the rate of
              <br />
              energy efficiency
              <br />
              improvement
            </h3>
            <p className="text-[10px] lg:text-[11px] text-gray-700 mt-1 mb-2 text-center leading-tight">
              Improvement Rate
              <br />
              (% per year)
            </p>
            <div className="w-full flex-1 relative min-h-[100px]">
              {show && (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={dataChart1}
                    margin={{ top: 15, right: 0, left: -30, bottom: 15 }}
                  >
                    <XAxis
                      dataKey="name"
                      tick={<CustomXAxisTick />}
                      axisLine={{ stroke: "#9CA3AF" }}
                      tickLine={false}
                      interval={0}
                    />
                    <YAxis
                      domain={[0, 6]}
                      ticks={[0, 2, 4, 6]}
                      axisLine={{ stroke: "#9CA3AF" }}
                      tickLine={false}
                      tickFormatter={(v) => `${v}%`}
                      tick={{ fill: "#4B5563", fontSize: 9 }}
                    />
                    <Bar dataKey="value" barSize={25} isAnimationActive={true}>
                      {dataChart1.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                      <LabelList
                        dataKey="displayValue"
                        position="top"
                        fill="#333"
                        fontSize={11}
                        fontWeight="bold"
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
              {/* Arrow Overlay */}
              {show && (
                <div className="absolute top-[35%] left-[28%] pointer-events-none flex flex-col items-center">
                  <span className="text-[#48DBB2] font-bold text-[12px] leading-none -ml-3 mb-1">
                    2X
                  </span>
                  <svg width="24" height="24" viewBox="0 0 50 50" fill="none">
                    <path
                      d="M5,45 Q20,20 40,5"
                      stroke="#48DBB2"
                      strokeWidth="4"
                      strokeLinecap="round"
                    />
                    <polygon points="40,5 28,8 36,18" fill="#48DBB2" />
                  </svg>
                </div>
              )}
            </div>
          </div>

          {/* Chart 2 */}
          <div className="flex-1 flex flex-col relative border-r border-[#003350] px-1 h-full">
            <h3 className="text-[#003350] font-bold text-center text-[12px] lg:text-[14px] leading-[1.15] min-h-[50px] flex items-center justify-center">
              Reduce energy
              <br />
              intensity by
            </h3>
            <p className="text-[10px] lg:text-[11px] text-gray-700 mt-1 mb-2 text-center leading-tight">
              Energy Intensity
              <br />
              (Index)
            </p>
            <div className="w-full flex-1 relative min-h-[100px]">
              {show && (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={dataChart2}
                    margin={{ top: 15, right: 0, left: -30, bottom: 15 }}
                  >
                    <XAxis
                      dataKey="name"
                      tick={<CustomXAxisTick />}
                      axisLine={{ stroke: "#9CA3AF" }}
                      tickLine={false}
                      interval={0}
                    />
                    <YAxis
                      domain={[0, 120]}
                      ticks={[0, 40, 80, 120]}
                      axisLine={{ stroke: "#9CA3AF" }}
                      tickLine={false}
                      tick={{ fill: "#4B5563", fontSize: 9 }}
                    />
                    <Bar dataKey="value" barSize={25} isAnimationActive={true}>
                      {dataChart2.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                      <LabelList
                        dataKey="displayValue"
                        position="top"
                        fill="#333"
                        fontSize={11}
                        fontWeight="bold"
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
              {/* Arrow Overlay */}
              {show && (
                <div className="absolute top-[20%] left-[55%] pointer-events-none flex flex-col items-center">
                  <span className="text-[#003350] font-bold text-[11px] leading-tight text-center">
                    -2.8%
                    <br />
                    <span className="text-[9px]">annually</span>
                  </span>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="mt-0.5"
                  >
                    <line
                      x1="4"
                      y1="4"
                      x2="18"
                      y2="18"
                      stroke="#003350"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                    <polygon points="18,18 10,18 18,10" fill="#003350" />
                  </svg>
                </div>
              )}
            </div>
          </div>

          {/* Chart 3 */}
          <div className="flex-1 flex flex-col relative border-r border-[#003350] px-1 h-full">
            <h3 className="text-[#003350] font-bold text-center text-[12px] lg:text-[14px] leading-[1.15] min-h-[50px] flex items-center justify-center">
              Reduce system
              <br />
              losses from
              <br />
              23% to 15%
            </h3>
            <p className="text-[10px] lg:text-[11px] text-gray-700 mt-1 mb-2 text-center leading-tight">
              System Losses
              <br />
              (%)
            </p>
            <div className="w-full flex-1 relative min-h-[100px]">
              {show && (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={dataChart3}
                    margin={{ top: 15, right: 0, left: -30, bottom: 15 }}
                  >
                    <XAxis
                      dataKey="name"
                      tick={<CustomXAxisTick />}
                      axisLine={{ stroke: "#9CA3AF" }}
                      tickLine={false}
                      interval={0}
                    />
                    <YAxis
                      domain={[0, 30]}
                      ticks={[0, 10, 20, 30]}
                      axisLine={{ stroke: "#9CA3AF" }}
                      tickLine={false}
                      tickFormatter={(v) => `${v}%`}
                      tick={{ fill: "#4B5563", fontSize: 9 }}
                    />
                    <Bar dataKey="value" barSize={25} isAnimationActive={true}>
                      {dataChart3.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                      <LabelList
                        dataKey="displayValue"
                        position="top"
                        fill="#333"
                        fontSize={11}
                        fontWeight="bold"
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
              {/* Arrow Overlay */}
              {show && (
                <div className="absolute top-[35%] left-[50%] pointer-events-none">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <line
                      x1="4"
                      y1="4"
                      x2="18"
                      y2="18"
                      stroke="#48DBB2"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                    <polygon points="18,18 10,18 18,10" fill="#48DBB2" />
                  </svg>
                </div>
              )}
            </div>
          </div>

          {/* Chart 4 */}
          <div className="flex-1 flex flex-col relative px-1 h-full">
            <h3 className="text-[#003350] font-bold text-center text-[12px] lg:text-[14px] leading-[1.15] min-h-[50px] flex items-center justify-center">
              Each 1% saved delivers
              <br />
              the same impact as
              <br />a new power plant
            </h3>
            <p className="text-[10px] lg:text-[11px] text-gray-700 mt-1 mb-2 text-center leading-tight">
              Impact
              <br />
              (Equivalent Power Plant)
            </p>
            <div className="w-full flex-1 relative min-h-[100px]">
              {show && (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={dataChart4}
                    margin={{ top: 15, right: 0, left: -30, bottom: 15 }}
                  >
                    <XAxis
                      dataKey="name"
                      tick={<CustomXAxisTick />}
                      axisLine={{ stroke: "#9CA3AF" }}
                      tickLine={false}
                      interval={0}
                    />
                    <YAxis
                      domain={[0, 2]}
                      ticks={[0, 1, 2]}
                      axisLine={{ stroke: "#9CA3AF" }}
                      tickLine={false}
                      tick={{ fill: "#4B5563", fontSize: 9 }}
                    />
                    <Bar dataKey="value" barSize={25} isAnimationActive={true}>
                      {dataChart4.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                      <LabelList
                        dataKey="displayValue"
                        position="top"
                        fill="#333"
                        fontSize={11}
                        fontWeight="bold"
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
              {/* Lightning Bolt Overlay */}
              {show && (
                <div className="absolute top-[15%] right-[20%] pointer-events-none">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="#003350"
                  >
                    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                  </svg>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Timeline Arrow */}
        <div className="w-full flex items-center mt-3 px-2">
          <span className="text-[#1a4a44] font-bold text-sm lg:text-base mr-3">
            2025
          </span>
          <div className="flex-1 h-1.5 bg-[#1a4a44] relative rounded-l-full">
            <div className="absolute -right-2 top-1/2 transform -translate-y-1/2 w-0 h-0 border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent border-l-[12px] border-l-[#1a4a44]"></div>
          </div>
          <span className="text-[#1a4a44] font-bold text-sm lg:text-base ml-4">
            2030
          </span>
        </div>
      </div>
    </div>
  );
}
